import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const SEEN_KEY = "lpg_notif_seen_v1";
const PERMISSION_ASKED_KEY = "lpg_notif_permission_asked_v1";
const POLL_MS = 30 * 1000; // 30 seconds
const MAX_SEEN = 500;

/** Load the seen set from localStorage. */
function loadSeen() {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw));
  } catch { return new Set(); }
}

function saveSeen(set) {
  try {
    // Cap at MAX_SEEN, keep newest
    const arr = Array.from(set);
    const trimmed = arr.slice(Math.max(0, arr.length - MAX_SEEN));
    localStorage.setItem(SEEN_KEY, JSON.stringify(trimmed));
  } catch { /* ignore */ }
}

/**
 * Global notification poller. Uses the browser Notification API to show OS-level
 * toasts when new events arrive. Runs whenever the user is logged in.
 * The very first time we run we (a) request Notification permission if not yet
 * granted, and (b) mark all currently-open events as "seen" so we don't spam the
 * user with the entire backlog.
 */
export function useNotifications() {
  const { user } = useAuth();
  const [permission, setPermission] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "default"
  );
  const [count, setCount] = useState(0);
  const seenRef = useRef(loadSeen());
  const bootstrappedRef = useRef(false);

  // Ask for permission once per browser (not every render)
  useEffect(() => {
    if (!user) return;
    if (typeof Notification === "undefined") return;
    if (Notification.permission !== "default") {
      setPermission(Notification.permission);
      return;
    }
    if (localStorage.getItem(PERMISSION_ASKED_KEY)) return;
    // Delay by 3s so we don't ask on the login flash
    const t = setTimeout(() => {
      Notification.requestPermission().then((p) => {
        setPermission(p);
        localStorage.setItem(PERMISSION_ASKED_KEY, "1");
      }).catch(() => {});
    }, 3000);
    return () => clearTimeout(t);
  }, [user]);

  // Poll for events
  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const fire = (ev) => {
      if (typeof Notification === "undefined") return;
      if (Notification.permission !== "granted") return;
      try {
        const n = new Notification(ev.title, {
          body: ev.body,
          icon: "/favicon.ico",
          tag: ev.id,     // dedupes at the OS layer within the same page
          silent: false,
        });
        n.onclick = () => {
          try { window.focus(); } catch { /* noop */ }
          if (ev.url) window.location.hash = ev.url;
          n.close();
        };
      } catch { /* noop */ }
    };

    const poll = async () => {
      try {
        const { data } = await api.get("/notifications/unseen");
        if (cancelled) return;
        const events = data.events || [];
        setCount(events.length);
        if (!bootstrappedRef.current) {
          // First poll: mark everything as already seen (no spam)
          events.forEach((e) => seenRef.current.add(e.id));
          saveSeen(seenRef.current);
          bootstrappedRef.current = true;
          return;
        }
        // Fire for events not yet seen
        for (const e of events) {
          if (!seenRef.current.has(e.id)) {
            fire(e);
            seenRef.current.add(e.id);
          }
        }
        saveSeen(seenRef.current);
      } catch { /* ignore polling errors */ }
    };

    // Kick immediately, then interval
    poll();
    const id = setInterval(poll, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [user]);

  const requestPermission = async () => {
    if (typeof Notification === "undefined") return "denied";
    const p = await Notification.requestPermission();
    setPermission(p);
    localStorage.setItem(PERMISSION_ASKED_KEY, "1");
    return p;
  };

  return { permission, requestPermission, unseenCount: count };
}
